import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useErrors from '../../../../../../hooks/useErrors';
import formatCep from '../../../../../../utils/formatCep';
import formatCnpj from '../../../../../../utils/formatCnpj';
import formatCpf from '../../../../../../utils/formatCpf';
import formatPhone from '../../../../../../utils/formatPhone';
import getCepData from '../../../../../../utils/getCepData';
import hasSequentialNumbers from '../../../../../../utils/hasSequentialNumbers';
import isCnpjValid from '../../../../../../utils/isCnpjValid';
import isCpfvalid from '../../../../../../utils/isCpfValid';
import isEmailValid from '../../../../../../utils/isEmailValid';
import onlyNumbers from '../../../../../../utils/onlyNumbers';

export default function useInputChanges() {
  const [companyName, setCompanyName] = useState('');
  const [fantasyName, setFantasyName] = useState('');
  const [employeesQty, setEmployeesQty] = useState<number | string>('');
  const [stateRegistration, setStateRegistration] = useState('');
  const [municipalRegistration, setMunicipalRegistration] = useState('');

  const [isGettingCepData, setIsGettingCepData] = useState(false);
  const [cep, setCep] = useState('');
  const [uf, setUf] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState<number | string>('');
  const [complement, setComplement] = useState('');
  const [companyCellphone, setCompanyCellphone] = useState('');

  const [billingAddress, setBillingAddres] = useState('same');
  const [isGettingBillingCepData, setIsGettingBillingCepData] = useState(false);
  const [billingCep, setBillingCep] = useState('');
  const [billingUf, setBillingUf] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingDistrict, setBillingDistrict] = useState('');
  const [billingStreet, setBillingStreet] = useState('');
  const [billingNumber, setBillingNumber] = useState<number | string>('');
  const [billingComplement, setBillingComplement] = useState('');

  const [name, setName] = useState('');
  const [contactCellphone, setContactCellphone] = useState('');
  const [email, setEmail] = useState('');
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [cnpjPagto, setCnpjPagto] = useState('');
  const [pagadorNFSeparada, setPagadorNFSeparada] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  function handleCompanyNameChange(event: ChangeEvent<HTMLInputElement>) {
    setCompanyName(event.target.value);
    if (!event.target.value) {
      setError({ field: 'companyName', message: 'Razão social é obrigatório!' });
    } else {
      removeError('companyName');
    }
  }

  function handleFantasyNameChange(event: ChangeEvent<HTMLInputElement>) {
    setFantasyName(event.target.value);
    if (!event.target.value) {
      setError({ field: 'fantasyName', message: 'Nome fantasia é obrigatório!' });
    } else {
      removeError('fantasyName');
    }
  }
  function handleEmployeesQtyChange(event: ChangeEvent<HTMLInputElement>) {
    setEmployeesQty(onlyNumbers(event.target.value));
    if (!event.target.value) {
      setError({ field: 'employeesQty', message: 'Número de funcionários é obrigatório!' });
    } else if (Number(event.target.value) === 0) {
      setError({ field: 'employeesQty', message: 'Número de funcionários não pode ser 0' });
    } else {
      removeError('employeesQty');
    }
  }

  function handleStateRegistrationChange(event: ChangeEvent<HTMLInputElement>) {
    setStateRegistration(event.target.value);
  }

  function handleMunicipalRegistrationChange(event: ChangeEvent<HTMLInputElement>) {
    setMunicipalRegistration(event.target.value);
  }
  function handleCompanyCellphoneChange(event: ChangeEvent<HTMLInputElement>) {
    setCompanyCellphone(formatPhone(event.target.value));

    if (!event.target.value) {
      setError({ field: 'companyCellphone', message: 'Telefone é obrigatório!' });
    } else if (event.target.value.length < 9) {
      setError({ field: 'companyCellphone', message: 'Telefone inválido' });
    } else {
      removeError('companyCellphone');
    }
  }

  function handleNumberChange(event: ChangeEvent<HTMLInputElement>) {
    setNumber(onlyNumbers(event.target.value));
  }

  function handleComplementChange(event: ChangeEvent<HTMLInputElement>) {
    setComplement(event.target.value);
  }

  function handleBillingNumberChange(event: ChangeEvent<HTMLInputElement>) {
    setBillingNumber(onlyNumbers(event.target.value));
  }

  function handleBillingComplementChange(event: ChangeEvent<HTMLInputElement>) {
    setBillingComplement(event.target.value);
  }

  function handleNameChange(event: ChangeEvent<HTMLInputElement>) {
    setName(event.target.value);

    if (!event.target.value) {
      setError({ field: 'name', message: 'Nome é obrigatório!' });
    } else {
      removeError('name');
    }
  }
  function handleContactCellphoneChange(event: ChangeEvent<HTMLInputElement>) {
    setContactCellphone(formatPhone(event.target.value));

    if (!event.target.value) {
      setError({ field: 'contactCellphone', message: 'Telefone do contato é obrigatório!' });
    } else if (event.target.value.length < 9) {
      setError({ field: 'contactCellphone', message: 'Telefone inválido!' });
    } else {
      removeError('contactCellphone');
    }
  }

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value);

    if (!isEmailValid(event.target.value) || !event.target.value) {
      setError({ field: 'email', message: 'E-mail inválido!' });
    } else if (event.target.value) {
      removeError('email');
    } if (event.target.value !== emailConfirmation) {
      setError({ field: 'emailConfirmation', message: 'E-mails divergentes' });
    } else if (event.target.value === emailConfirmation) {
      removeError('emailConfirmation');
    }
  }

  function handleEmailConfirmationChange(event: ChangeEvent<HTMLInputElement>) {
    setEmailConfirmation(event.target.value);

    if (event.target.value !== email) {
      setError({ field: 'emailConfirmation', message: 'E-mails divergentes' });
    } else {
      removeError('emailConfirmation');
    }
  }

  function handleCpfChange(event: ChangeEvent<HTMLInputElement>) {
    setCpf(formatCpf(event.target.value));
    if (!event.target.value || !isCpfvalid(formatCpf(event.target.value))) {
      setError({ field: 'cpf', message: 'Informe um cpf válido!' });
    } else {
      removeError('cpf');
    }
  }

  function handleCnpjChange(event: ChangeEvent<HTMLInputElement>) {
    setCnpj(formatCnpj(event.target.value));
    if (!event.target.value || !isCnpjValid(formatCnpj(event.target.value))) {
      setError({ field: 'cnpj', message: 'Informe um CNPJ válido!' });
    } else {
      removeError('cnpj');
    }
  }

  function handleCnpjPagtoChange(event: ChangeEvent<HTMLInputElement>) {
    const formatted = formatCnpj(event.target.value.slice(0, 20));
    setCnpjPagto(formatted);
    if (formatted && !isCnpjValid(formatted)) {
      setError({ field: 'cnpjPagto', message: 'Informe um CNPJ de pagamento válido!' });
    } else {
      removeError('cnpjPagto');
    }
  }

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value);

    if (event.target.value.length < 8 || hasSequentialNumbers(event.target.value)) {
      setError({ field: 'password', message: 'Senha deve conter ter 8 caracteres, e não pode haver números sequenciais!' });
    } else if (event.target.value) {
      removeError('password');
    } if (event.target.value !== passwordConfirmation) {
      setError({ field: 'passwordConfirmation', message: 'Senhas divergentes' });
    } else if (event.target.value === passwordConfirmation) {
      removeError('passwordConfirmation');
    }
  }

  function handlePasswordConfirmationChange(event: ChangeEvent<HTMLInputElement>) {
    setPasswordConfirmation(event.target.value);

    if (event.target.value !== password) {
      setError({ field: 'passwordConfirmation', message: 'Senhas divergentes' });
    } else {
      removeError('passwordConfirmation');
    }
  }

  const handleCepChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      setCep(formatCep(event.target.value));
      if (!event.target.value) {
        setError({ field: 'cep', message: 'CEP é obrigatório!' });
      }
      if (formatCep(event.target.value).length === 9) {
        setIsGettingCepData(true);
        const cepInfo = await getCepData(event.target.value);
        if (cepInfo.erro) {
          setError({ field: 'cep', message: 'CEP inválido!' });
          setStreet('');
          setDistrict('');
          setCity('');
          setUf('');
          return;
        }
        setStreet(cepInfo.logradouro);
        setDistrict(cepInfo.bairro);
        setCity(cepInfo.localidade);
        setUf(cepInfo.uf);
      }
      if (formatCep(event.target.value).length !== 9) {
        setError({ field: 'cep', message: 'CEP inválido!' });
        setStreet('');
        setDistrict('');
        setCity('');
        setUf('');
        return;
      }
      removeError('cep');
    } catch (error) {
      toast.error(`Ocorreu um erro ao buscar o CEP (${error})`);
    } finally {
      setIsGettingCepData(false);
    }
  }, [removeError, setError]);

  const handleBillingCepChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      setBillingCep(formatCep(event.target.value));
      if (!event.target.value) {
        setError({ field: 'billingCep', message: 'CEP é obrigatório!' });
      }
      if (formatCep(event.target.value).length === 9) {
        setIsGettingBillingCepData(true);
        const cepInfo = await getCepData(event.target.value);
        if (cepInfo.erro) {
          setError({ field: 'cep', message: 'CEP inválido!' });
          setBillingStreet('');
          setBillingDistrict('');
          setBillingCity('');
          setBillingUf('');
          return;
        }
        setBillingStreet(cepInfo.logradouro);
        setBillingDistrict(cepInfo.bairro);
        setBillingCity(cepInfo.localidade);
        setBillingUf(cepInfo.uf);
      }
      if (formatCep(event.target.value).length !== 9) {
        setError({ field: 'cep', message: 'CEP inválido!' });
        setBillingStreet('');
        setBillingDistrict('');
        setBillingCity('');
        setBillingUf('');
        return;
      }
      removeError('cep');
    } catch (error) {
      toast.error(`Ocorreu um erro ao buscar o CEP (${error})`);
    } finally {
      setIsGettingBillingCepData(false);
    }
  }, [removeError, setError]);

  return {
    getErrorMessageByFieldName,
    cpf,
    handleCpfChange,
    cnpj,
    handleCnpjChange,
    cnpjPagto,
    handleCnpjPagtoChange,
    pagadorNFSeparada,
    setPagadorNFSeparada,
    password,
    handlePasswordChange,
    passwordConfirmation,
    handlePasswordConfirmationChange,
    email,
    handleEmailChange,
    emailConfirmation,
    handleEmailConfirmationChange,
    companyName,
    handleCompanyNameChange,
    fantasyName,
    handleFantasyNameChange,
    employeesQty,
    handleEmployeesQtyChange,
    stateRegistration,
    handleStateRegistrationChange,
    municipalRegistration,
    handleMunicipalRegistrationChange,
    companyCellphone,
    handleCompanyCellphoneChange,
    cep,
    handleCepChange,
    uf,
    city,
    district,
    street,
    handleNumberChange,
    number,
    handleComplementChange,
    complement,
    setBillingAddres,
    billingAddress,
    billingCep,
    handleBillingCepChange,
    billingUf,
    billingCity,
    billingDistrict,
    billingStreet,
    handleBillingNumberChange,
    billingNumber,
    handleBillingComplementChange,
    billingComplement,
    name,
    handleNameChange,
    contactCellphone,
    handleContactCellphoneChange,
    isGettingCepData,
    isGettingBillingCepData,
    errors,
  };
}
